import React, { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import NextImage, { StaticImageData } from 'next/image';
import Robot01StillSrc from '@/assets/robot-01.png';
import Robot02LeftSrc from '@/assets/robot-02.png';
import Robot03RightSrc from '@/assets/robot-03.png';
import Robot04Together1Src from '@/assets/robot-04.png';
import Robot05Together2Src from '@/assets/robot-05.png';
import Robot06Together3Src from '@/assets/robot-06.png';

export const ALL_ROBOT_FRAMES: StaticImageData[] = [
  Robot01StillSrc,
  Robot02LeftSrc,
  Robot03RightSrc,
  Robot04Together1Src,
  Robot05Together2Src,
  Robot06Together3Src,
];

const decodeCache = new Map<string, Promise<void>>();

const decodeOnce = (src: string) => {
  if (!decodeCache.has(src)) {
    const p =
      typeof window === 'undefined'
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            const img = new Image();

            let settled = false;
            const done = () => {
              if (!settled) {
                settled = true;
                resolve();
              }
            };

            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });

            (img as any).decode?.().then(done).catch(done);

            img.src = src;
          });

    decodeCache.set(src, p);
  }

  return decodeCache.get(src)!;
};

export function usePreloadImages(sources: StaticImageData[]) {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    Promise.all(
      sources.map((s) => decodeOnce(typeof s === 'string' ? s : s.src)),
    ).then(() => {
      if (!cancelled) {
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sources]);

  return ready;
}

export type RobotType =
  | 'still'
  | 'left'
  | 'right'
  | 'together'
  | 'together1'
  | 'together2'
  | 'together3';

interface RobotProps {
  className?: string;
  type?: RobotType;
}

const getRobotImageSrc = (type: RobotProps['type']) => {
  switch (type) {
    default:
    case 'still':
      return Robot01StillSrc;
    case 'left':
      return Robot02LeftSrc;
    case 'right':
      return Robot03RightSrc;
    case 'together1':
      return Robot04Together1Src;
    case 'together2':
      return Robot05Together2Src;
    case 'together3':
      return Robot06Together3Src;
    case 'together':
      return [Robot04Together1Src, Robot05Together2Src, Robot06Together3Src];
  }
};
export const Robot = ({ className, type = 'still' }: RobotProps) => {
  const [currentImage, setCurrentImage] = useState<
    StaticImageData | undefined
  >();
  const currentImages = useMemo(() => getRobotImageSrc(type), [type]);

  const ready = usePreloadImages(ALL_ROBOT_FRAMES);

  useEffect(() => {
    if (!ready) {
      return;
    }

    let timeoutId: NodeJS.Timeout;
    if (Array.isArray(currentImages)) {
      let index = 0;
      timeoutId = setInterval(() => {
        setCurrentImage(currentImages[index]);
        index = (index + 1) % currentImages.length;
      }, 300);
    } else {
      setCurrentImage(currentImages);
    }

    return () => {
      if (timeoutId) {
        clearInterval(timeoutId);
      }
    };
  }, [currentImages]);

  return (
    <div className={clsx('relative', className)}>
      {currentImage && (
        <NextImage
          src={currentImage}
          alt="Pickatale Robot"
          width={Robot01StillSrc.width}
          height={Robot01StillSrc.height}
          className="w-full h-auto"
        />
      )}
    </div>
  );
};
